export type BarcodeFormat = { pattern?: string; placeholder?: string }
export type BarcodeFormatsConfig = Record<string, BarcodeFormat | BarcodeFormat[]>

export enum SectionState {
  LOCKED = 'locked',
  UNLOCKED = 'unlocked',
  EDITING = 'editing',
}

/** Two-column field pairs for the claimant section (read-only view) */
export const CLAIMANT_ROWS: [string, string | null][] = [
  ['firstName', 'middleInitial'],
  ['lastName', 'suffix'],
  ['socialSecurityNumber', 'dateOfBirth'],
  ['usCitizen', 'originalClaimant'],
  ['countryOfResidence', null],
]

/** Two-column field pairs for the claimant section (editable view, minus countryOfResidence handled separately) */
export const EDITABLE_CLAIMANT_ROWS: [string, string | null][] = [
  ['firstName', 'middleInitial'],
  ['lastName', 'suffix'],
  ['socialSecurityNumber', 'dateOfBirth'],
  ['usCitizen', 'originalClaimant'],
]

/** Two-column field pairs for the contact section (read-only view) */
export const CONTACT_ROWS: [string, string | null][] = [
  ['mailingAddress1', 'mailingAddress2'],
  ['region', 'postalCode'],
  ['mobilePhoneNumber', 'secondaryPhoneNumber'],
  ['emailAddress', null],
]

/** Two-column field pairs for the contact section (editable view) */
export const EDITABLE_CONTACT_ROWS: [string, string | null][] = [
  ['mailingAddress1', 'mailingAddress2'],
  ['city', 'region'],
  ['postalCode', null],
  ['mobilePhoneNumber', 'secondaryPhoneNumber'],
  ['emailAddress', null],
]

/**
 * Short display labels for the back-office, only for fields where the schema's
 * player-facing label is too long or verbose. All other fields use the schema label directly.
 */
const BACK_OFFICE_LABEL_OVERRIDES: Record<string, string> = {
  usCitizen: 'US Citizen?',
  originalClaimant: 'Original Claimant?',
  socialSecurityNumber: 'SSN',
  emailAddress: 'Email',
}

/**
 * Get a display label for a field. Checks back-office overrides first,
 * then the schema label, then falls back to formatting the field key.
 */
export function getFieldLabel(
  fieldKey: string,
  schema?: Record<string, unknown>,
  sectionKey?: string,
): string {
  if (BACK_OFFICE_LABEL_OVERRIDES[fieldKey]) {
    return BACK_OFFICE_LABEL_OVERRIDES[fieldKey]
  }

  if (schema && sectionKey) {
    const properties = schema?.properties as Record<string, unknown> | undefined
    const section = properties?.[sectionKey] as Record<string, unknown> | undefined
    const sectionProps = section?.properties as Record<string, unknown> | undefined
    const field = sectionProps?.[fieldKey] as Record<string, unknown> | undefined
    if (field?.label) {
      return field.label as string
    }
  }

  // Fallback: convert camelCase to Title Case
  return fieldKey.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())
}

export type InputType = 'date' | 'datetime-local' | 'email' | 'number' | 'tel' | 'text'

export function formatBarcode(value: string, placeholder: string): string {
  const digits = value.replace(/\D/g, '')
  let result = ''
  let digitIndex = 0
  for (let i = 0; i < placeholder.length && digitIndex < digits.length; i++) {
    if (placeholder[i] === '-') {
      result += '-'
    } else {
      result += digits[digitIndex]
      digitIndex++
    }
  }
  return result
}
